/** Pick team members as text / email recipients with one tap; manual entry kept for outside numbers. */
import React, { useState } from 'react';
import { Mail, Phone, Plus, UserPlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

const muteKey = (v, kind) => (kind === 'sms' ? normPhone(v) : normEmail(v));
const isMuted = (muted, v, kind) => (muted || []).some((m) => muteKey(m, kind) === muteKey(v, kind));
const toggleMuted = (muted, v, kind, on) => (on ? (muted || []).filter((m) => muteKey(m, kind) !== muteKey(v, kind)) : [...(muted || []), v]);

export const normPhone = (v) => {
  let d = String(v || '').replace(/[^\d+]/g, '');
  if (d.startsWith('+44')) d = `0${d.slice(3)}`;
  else if (d.startsWith('44') && d.length === 12) d = `0${d.slice(2)}`;
  return d;
};
const normEmail = (v) => String(v || '').trim().toLowerCase();

export function personFor(people, value, kind) {
  const v = kind === 'sms' ? normPhone(value) : normEmail(value);
  return (people || []).find((p) => (kind === 'sms' ? normPhone(p.phone) === v : normEmail(p.email) === v));
}

function Chip({ label, sub, onRemove, testId, tone = 'bg-gray-100 border-gray-200 text-gray-800', on, onToggle }) {
  const off = onToggle && on === false;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border pl-2.5 pr-1.5 py-1 text-xs ${off ? 'bg-gray-50 border-dashed border-gray-300 text-gray-400' : tone}`} data-testid={testId}>
      {onToggle && <Switch checked={!!on} onCheckedChange={onToggle} className="scale-75 -ml-1" aria-label={`${label} on/off`} data-testid={`${testId}-toggle`} />}
      <span className={`font-semibold ${off ? 'line-through' : ''}`}>{label}</span>
      {sub && <span className="text-[11px] opacity-70">{sub}</span>}
      {off && <span className="text-[10px] font-bold uppercase text-gray-400">off</span>}
      {onRemove && <button type="button" onClick={onRemove} className="rounded-full p-0.5 text-gray-400 hover:text-rose-600 hover:bg-white" aria-label={`Remove ${label}`}><X className="w-3 h-3" /></button>}
    </span>
  );
}

export function RecipientList({ kind, items, people, onChange, testId, label, muted, onMuted }) {
  const [manual, setManual] = useState(false);
  const [v, setV] = useState('');
  const Icon = kind === 'sms' ? Phone : Mail;
  const value = (p) => (kind === 'sms' ? p.phone : p.email);
  const has = (p) => items.some((it) => (kind === 'sms' ? normPhone(it) === normPhone(value(p)) : normEmail(it) === normEmail(value(p))));
  const available = (people || []).filter((p) => value(p) && !has(p));
  const missing = kind === 'sms' ? (people || []).filter((p) => !p.phone) : [];
  const addManual = () => { const t = v.trim(); if (!t) return; onChange([...items, t]); setV(''); setManual(false); };
  return (
    <div className="min-w-[240px] flex-1" data-testid={testId}>
      <div className="text-[11px] font-semibold text-gray-600 mb-1.5 inline-flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.map((it) => {
          const p = personFor(people, it, kind);
          return <Chip key={it} label={p ? p.name : it} sub={p ? it : null} onRemove={() => onChange(items.filter((x) => x !== it))} testId={`${testId}-item`}
            on={onMuted ? !isMuted(muted, it, kind) : undefined} onToggle={onMuted ? (v) => onMuted(toggleMuted(muted, it, kind, v)) : undefined} />;
        })}
        {!items.length && <span className="text-xs text-gray-400 italic">Nobody yet</span>}
      </div>
      <div className="flex flex-wrap gap-1.5 items-center">
        <select value="" onChange={(e) => { const p = available.find((x) => x.user_id === e.target.value); if (p) onChange([...items, value(p)]); }}
          className="h-8 rounded-md border bg-white px-2 text-xs max-w-[240px]" aria-label={`Add a team member to ${label}`} data-testid={`${testId}-pick`}>
          <option value="">{available.length ? '+ Add a team member…' : missing.length ? 'Team members need a mobile saved first' : 'Everyone on the team is added'}</option>
          {available.map((p) => <option key={p.user_id} value={p.user_id}>{p.name} · {value(p)}</option>)}
          {missing.length > 0 && <optgroup label="No mobile saved on their profile">{missing.map((p) => <option key={p.user_id} value="" disabled>{p.name}</option>)}</optgroup>}
        </select>
        {!manual && <button type="button" onClick={() => setManual(true)} className="text-[11px] font-semibold text-violet-700 hover:underline inline-flex items-center gap-1" data-testid={`${testId}-manual`}><UserPlus className="w-3 h-3" /> {kind === 'sms' ? 'Type a number' : 'Type an email'}</button>}
        {manual && (<>
          <input autoFocus value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addManual(); } if (e.key === 'Escape') setManual(false); }}
            placeholder={kind === 'sms' ? '07xxx xxxxxx' : 'name@example.com'} className="h-8 w-44 rounded-md border px-2 text-xs bg-white" data-testid={`${testId}-input`} />
          <Button type="button" size="sm" variant="outline" className="h-8" onClick={addManual} data-testid={`${testId}-add`}><Plus className="w-3.5 h-3.5" /></Button>
        </>)}
      </div>
    </div>
  );
}

export function WhoGetsThis({ r, people, testId }) {
  if (!r) return null;
  const texts = [];
  const emailsOnly = [];
  const muted = r.muted || [];
  (r.auto || []).forEach((p) => { if (p.sms_on === false) return; (p.phone ? texts : emailsOnly).push(p.name); });
  (r.sms_numbers || []).forEach((n) => { if (isMuted(muted, n, 'sms')) return; const p = personFor(people, n, 'sms'); const l = p ? p.name : n; if (!texts.includes(l)) texts.push(l); });
  const push = r.people || [];
  const emails = (r.emails || []).filter((e) => !isMuted(muted, e, 'email')).length;
  const offCount = (r.auto || []).filter((p) => p.sms_on === false).length + (r.sms_numbers || []).filter((n) => isMuted(muted, n, 'sms')).length + (r.emails || []).filter((e) => isMuted(muted, e, 'email')).length;
  const parts = [];
  if (texts.length) parts.push(<span key="t"><Phone className="w-3 h-3 inline -mt-0.5 mr-1" /><b>Text:</b> {texts.join(', ')}</span>);
  if (emailsOnly.length) parts.push(<span key="eo" className="text-amber-700"><b>Email only (no mobile):</b> {emailsOnly.join(', ')}</span>);
  if (emails) parts.push(<span key="e"><Mail className="w-3 h-3 inline -mt-0.5 mr-1" /><b>Email:</b> {emails} address{emails === 1 ? '' : 'es'}</span>);
  if (push.length) parts.push(<span key="p"><b>Push:</b> {push.join(', ')}</span>);
  if (offCount) parts.push(<span key="off" className="text-gray-500">{offCount} switched off</span>);
  return (
    <div className="mt-2 rounded-lg bg-violet-50/70 border border-violet-100 px-3 py-1.5 text-xs text-violet-950 flex flex-wrap gap-x-4 gap-y-1" data-testid={testId}>
      <span className="font-bold uppercase tracking-wider text-[10px] text-violet-700 self-center">Who gets this</span>
      {parts.length ? parts : <span className="italic text-gray-500">Nobody yet — add recipients below</span>}
    </div>
  );
}

export function AddMobileInline({ person, onSave, testId }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => { const t = v.trim(); if (!t) return; setBusy(true); try { await onSave(person.user_id, t); setOpen(false); setV(''); } finally { setBusy(false); } };
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="ml-0.5 rounded-full bg-amber-600 text-white px-2 py-0.5 text-[10px] font-bold hover:bg-amber-700" data-testid={testId}>Add mobile</button>;
  return (
    <span className="inline-flex items-center gap-1">
      <input autoFocus value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') setOpen(false); }}
        placeholder="07xxx xxxxxx" className="h-6 w-32 rounded border border-amber-300 bg-white px-1.5 text-[11px]" data-testid={`${testId}-input`} />
      <button type="button" disabled={busy} onClick={save} className="rounded-full bg-amber-600 text-white px-2 py-0.5 text-[10px] font-bold disabled:opacity-50" data-testid={`${testId}-save`}>Save</button>
    </span>
  );
}

export function AutoRecipients({ auto, onAddMobile, muted, onMuted, testId }) {
  const toggle = (p, on) => onMuted(on ? (muted || []).filter((m) => m !== p.user_id) : [...(muted || []), p.user_id]);
  if (!auto?.length) return null;
  return (
    <div className="w-full" data-testid={testId}>
      <div className="text-[11px] font-semibold text-gray-600 mb-1.5">Everyone with profit access — switch their text off without touching their access</div>
      <div className="flex flex-wrap gap-1.5">
        {auto.map((p) => { const off = p.sms_on === false; return (
          <span key={p.user_id} className={`inline-flex items-center gap-1.5 rounded-full border pl-2 pr-1.5 py-1 text-xs ${off ? 'bg-gray-50 border-dashed border-gray-300 text-gray-400' : p.phone ? 'bg-violet-50 border-violet-200 text-violet-900' : 'bg-amber-50 border-amber-300 text-amber-900'}`} data-testid={`${testId}-item`}>
            {onMuted && p.phone && <Switch checked={!off} onCheckedChange={(v) => toggle(p, v)} className="scale-75 -ml-1" aria-label={`${p.name} text on/off`} data-testid={`${testId}-toggle-${p.user_id}`} />}
            <span className={`font-semibold ${off ? 'line-through' : ''}`}>{p.name}</span>
            <span className="text-[11px] opacity-70">{off ? 'text off' : p.phone || 'no mobile saved'}</span>
            {!p.phone && onAddMobile && <AddMobileInline person={p} onSave={onAddMobile} testId={`${testId}-add-mobile-${p.user_id}`} />}
          </span>
        ); })}
      </div>
    </div>
  );
}

export function MissingMobiles({ people, onAddMobile, testId }) {
  const [open, setOpen] = useState(false);
  const missing = (people || []).filter((p) => !p.phone);
  if (!missing.length || !onAddMobile) return null;
  return (
    <div className="w-full" data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="text-[11px] font-semibold text-amber-700 hover:underline" data-testid={`${testId}-toggle`}>
        {missing.length} team member{missing.length === 1 ? ' has' : 's have'} no mobile saved · {open ? 'hide' : 'add one'}
      </button>
      {open && <div className="flex flex-wrap gap-1.5 mt-1.5">
        {missing.map((p) => (
          <span key={p.user_id} className="inline-flex items-center gap-1.5 rounded-full border bg-amber-50 border-amber-300 text-amber-900 pl-2.5 pr-1.5 py-1 text-xs" data-testid={`${testId}-item`}>
            <span className="font-semibold">{p.name}</span>
            <AddMobileInline person={p} onSave={onAddMobile} testId={`${testId}-add-mobile-${p.user_id}`} />
          </span>
        ))}
      </div>}
    </div>
  );
}
